import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, FormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Claim } from '@app/core/interfaces/claim';
import { LocationInterface } from '@app/core/interfaces/location.interface';
import { ClaimAppointmentInterface } from '@app/my-zone/pages/claims/store/entity/appointment.interface';
import { ClaimEventInterface } from '@app/my-zone/pages/claims/store/entity/claim-event.interface';
import { ContentInterface, ContentService, DateService, Notification } from 'outshared-lib';
import { Observable, Subscription } from 'rxjs';

@Component({
    selector: 'ins-appointment-booking',
    templateUrl: './appointment-booking.component.html',
    styleUrls: ['./appointment-booking.component.scss'],
})
export class AppointmentBookingComponent implements OnInit, OnDestroy {
    public datum: string;
    public startDate = {};
    public form: UntypedFormGroup;
    public replaceableVehicles$: Observable<ContentInterface[]>;

    @Output() readonly submitted = new EventEmitter<{
        clearErrorPayload: {}, setAppointmentPayload: {
            adres_relatie_id: number,
            schadeherstel_gebeurtenis: any[],
            dossier_volgnummer: number,
            netwerk_id: string,
            claim_volgnummer: number,
            gebeurtenis: ClaimEventInterface,
            schadeherstel_soort_vervangend_vervoer_code: string,
        },
    }>();

    @Input() loading: boolean;
    @Input() notifications: Notification[];
    @Input() appointment: ClaimAppointmentInterface;
    @Input() fileSerialNumber: number;

    public location: LocationInterface;

    private subscriptions = new Subscription();

    constructor(private dateService: DateService,
                private contentService: ContentService) {
    }

    public get address_relation_id(): AbstractControl {
        return this.form.get('adres_relatie_id');
    }

    public get damage_adopted_indication(): AbstractControl {
        return this.form.get('schade_vastegesteld_indicatie');
    }

    public ngOnInit(): void {
        this.datum = this.dateService.nowAsString();
        this.startDate = this.dateService.getStartDate();
        this.createForm();
        this.replaceableVehicles$ = this.contentService.getContent$('schadeherstel_soort_vervangend_vervoer_code');
    }

    public ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    public onSelectLocation(location: LocationInterface): void {
        if (location) {
            this.location = location;
            this.address_relation_id.setValue(location.business_relation_id);
            this.form.controls.indicatie_n.setValue(null);
        }
    }

    public onSubmit(): void {
        if (!this.form.valid) {
            return;
        }

        const claimSerialNumber = this.filterClaim(this.appointment.claim);
        const indication = (this.form.value.schade_vastegesteld_indicatie === 'N') ?
            this.form.value.indicatie_n :
            this.form.value.indicatie_j;

        const damageRepairEvent = [{
            schadeherstel_gebeurtenis_code: this.appointment.gebeurtenis.schadeherstel_gebeurtenis_code,
            schadeherstel_gebeurtenis_datum: indication.datum,
            schadeherstel_gebeurtenis_tijdstip: indication.tijdstip,
            schadeherstel_gebeurtenis_volgnummer: 1,
        }];

        const damageEvent: ClaimEventInterface = Object.assign({}, this.appointment.gebeurtenis, this.location, {
            schadeherstel_gebeurtenis_datum: indication.datum,
            schadeherstel_gebeurtenis_tijdstip: indication.tijdstip,
        });

        this.submitted.emit({
            clearErrorPayload: { dossier_volgnummer: this.fileSerialNumber },
            setAppointmentPayload: {
                adres_relatie_id: this.form.value.adres_relatie_id,
                schadeherstel_gebeurtenis: damageRepairEvent,
                dossier_volgnummer: this.fileSerialNumber,
                netwerk_id: this.location.network_id,
                claim_volgnummer: claimSerialNumber,
                gebeurtenis: damageEvent,
                schadeherstel_soort_vervangend_vervoer_code: this.form.value.schadeherstel_soort_vervangend_vervoer_code,
            },
        });
    }

    private createForm(): void {
        this.form = new UntypedFormGroup({
            adres_relatie_id: new FormControl(null, [Validators.required]),
            schade_vastegesteld_indicatie: new FormControl('N'),
            indicatie_n: new FormControl(null, [Validators.required]),
            indicatie_j: new FormControl(null, [Validators.required]),
            schadeherstel_soort_vervangend_vervoer_code: new FormControl(null),
        });

        const subRelatieId = this.form
            .controls
            .adres_relatie_id
            .valueChanges
            .subscribe((val) => {
                if (val === null) {
                    this.form.controls.schade_vastegesteld_indicatie.disable();
                } else {
                    this.form.controls.schade_vastegesteld_indicatie.enable();
                }
            });

        const subSchadeIndicatie = this.form
            .controls
            .schade_vastegesteld_indicatie
            .valueChanges
            .subscribe((val) => {
                if (val === 'N') {
                    this.form.controls.indicatie_n.setValidators([Validators.required]);
                    this.form.controls.indicatie_n.updateValueAndValidity();

                    this.form.controls.indicatie_j.clearValidators();
                    this.form.controls.indicatie_j.updateValueAndValidity();
                } else {
                    this.form.controls.indicatie_n.clearValidators();
                    this.form.controls.indicatie_n.updateValueAndValidity();

                    this.form.controls.indicatie_j.setValidators([Validators.required]);
                    this.form.controls.indicatie_j.updateValueAndValidity();
                }
            });
        this.subscriptions.add(subRelatieId);
        this.subscriptions.add(subSchadeIndicatie);
    }

    private filterClaim(claim: Claim[]): number {
        let claim_volgnummer: number = null;

        if (!claim) {
            return claim_volgnummer;
        }

        claim.forEach((tmp) => {
            claim_volgnummer = tmp.claim_volgnummer;
        });

        return claim_volgnummer;
    }
}
